import jsPDF from "jspdf";
import { logoBase64 } from "../../../public/assets/logoBase64";
import { marca } from "../../../public/assets/estadoCuentaMarca";
import { footer } from "../../../public/assets/estadoCuentaFooter";
import { StatementResponse } from "../interfaces/gestioncobros.interfaces";

interface Params {
  data: StatementResponse[];
  fecha: Date;
}

interface AccountsStateMap {
  currencyRecords: Record<
    string, Record<string, {
      records: StatementResponse[],
      totalAmount: number,
      totalDebit: number,
      totalCredit: number
    }>>;
  totalCurrency: Record<string, number>;
}

/**
 * Agrupa los registros del estado de cuenta por moneda y número de póliza.
 * Calcula además los totales por póliza y por moneda.
 *
 * @param data - Lista de movimientos del estado de cuenta.
 * @returns Estructura agrupada por moneda y póliza con sus totales.
 */
const mapEstadoCuentas = (data: StatementResponse[]): AccountsStateMap => {
  const currencyRecords: AccountsStateMap["currencyRecords"] = {};
  const totalCurrency: AccountsStateMap["totalCurrency"] = {};

  data.forEach((record) => {
    const currentCurrency = record.currency;
    const policy = record.policyNumber;

    const debit = record.origin > 0 ? record.amount : 0;
    const credit = record.origin < 0 ? record.amount : 0;
    record = { ...record, debit, credit };

    if (!currencyRecords[currentCurrency]) {
      currencyRecords[currentCurrency] = {
        [policy]: {
          records: [record],
          totalAmount: record.amount,
          totalDebit: debit,
          totalCredit: credit
        }
      };
      totalCurrency[currentCurrency] = record.amount;
    } else {
      totalCurrency[currentCurrency] += record.amount;

      if (!currencyRecords[currentCurrency][policy]) {
        currencyRecords[currentCurrency][policy] = {
          records: [record],
          totalAmount: record.amount,
          totalDebit: debit,
          totalCredit: credit
        };
      } else {
        const policyGroup = currencyRecords[currentCurrency][policy];
        policyGroup.records.push(record);
        policyGroup.records.sort(sortByDates);
        policyGroup.totalAmount += record.amount;
        policyGroup.totalDebit += debit;
        policyGroup.totalCredit += credit;
      }
    }
  });

  return { currencyRecords, totalCurrency };
};

/**
 * Genera y exporta un archivo PDF con los estados de cuenta de un cliente.
 *
 * @param params - Parámetros para el reporte.
 * @param params.data - Lista de movimientos a exportar.
 * @param params.fecha - Fecha de corte del estado de cuenta.
 */
export const exportarEstadoCuentas = ({ data, fecha }: Params) => {
  const mappedData = mapEstadoCuentas(data);
  const doc = new jsPDF({ orientation: "portrait" }) as any;
  const pageHeight = doc.internal.pageSize.height;

  // Formateo de fecha (+1 día para ajustar zonas horarias)
  const fechaUpdate = new Date(fecha.setDate(fecha.getDate() + 1));
  const fechaFormat = fechaUpdate.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });

  // Datos del cliente (se asume que todos los registros tienen la misma info)
  const { debtorCode: clientCode, clientName, address: clientAddress, idNumber: clientRNC } = data[0];
  let currentY = 15;

  /**
   * Renderiza el encabezado de cada página con logo y datos del cliente.
   */
  const pageHeader = (doc: jsPDF) => {
    doc.addImage(logoBase64, "PNG", 20, 10, 50, 16);
    doc.addImage(marca, "PNG", 113, 10, 93, 45);

    doc.setFontSize(11);
    doc.setFont(undefined!, "bold");
    doc.text(`Estado de Cuenta Cliente al: ${fechaFormat}`, 5, 40);

    doc.text("Cliente:", 5, 50);
    doc.setFont(undefined!, "normal");
    doc.text(`${clientCode}     ${clientName}`, 30, 50);

    doc.setFont(undefined!, "bold");
    doc.text("Dirección:", 5, 56);
    doc.setFont(undefined!, "normal");
    doc.text(clientAddress, 30, 56);

    doc.setFont(undefined!, "bold");
    doc.text("RNC:", 5, 62);
    doc.setFont(undefined!, "normal");
    doc.text(clientRNC, 30, 62);

    currentY += 30;
  };

  Object.entries(mappedData.currencyRecords).forEach(([currency, policies], currencyIndex) => {
    const isLastCurrency = currencyIndex === Object.keys(mappedData.currencyRecords).length - 1;

    Object.entries(policies).forEach(([policy, policyData], policyIndex) => {
      const isLastPolicy = policyIndex === Object.keys(policies).length - 1;

      if (currentY + 55 > pageHeight) {
        doc.addPage();
        doc.autoTable({ didDrawPage: () => pageHeader(doc) });
        currentY = 15;
      }

      // Tabla de movimientos por póliza
      const body = policyData.records.map((record) => [
        record.documentNumber,
        record.transaction,
        record.transaction === "RECIBO" ? "" : record.sDate,
        record.wDate,
        record.debit ? formatAmount(record.debit) : null,
        record.credit ? formatAmount(record.credit) : null,
        ""
      ]);

      // Cabecera y resumen
      body.unshift([`${policy}`, `${policyData.records[0].lobDescription}`, "", "", "", "", ""]);
      body.unshift(["Póliza/#Doc.", "Tipo", "Inicio", "Emitido", "Débitos", "Créditos", "Balance"]);
      body.push([
        "", "", "", "",
        formatAmount(policyData.totalDebit),
        formatAmount(policyData.totalCredit),
        formatAmount(policyData.totalAmount)
      ]);

      // Total por moneda
      if (isLastPolicy) {
        body.push(["", "", "", "", "", "", ""]);
        body.push([
          "", `Balance corte en: ${currency}`, "", "", "", "",
          formatAmount(mappedData.totalCurrency[currency])
        ]);
      }

      doc.autoTable({
        body,
        margin: { left: 5, right: 5, top: 70 },
        headStyles: {
          fillColor: [0, 85, 140],
          textColor: [255, 255, 255],
          fontSize: 10
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "left" },
          2: { halign: "left" },
          3: { halign: "left" },
          4: { halign: "right" },
          5: { halign: "right" },
          6: { halign: "right" }
        },
        styles: {
          textColor: [0, 0, 0]
        },
        didParseCell: (data: any) => {
          const { row, cell, table } = data;
          const i = row.index;
          const last = table.body.length - 1;

          if (i && data.section === "body") cell.styles.fillColor = [255, 255, 255];
          if (i === 0) Object.assign(cell.styles, {
            lineWidth: { bottom: 1 },
            lineColor: [255, 165, 0],
            fillColor: [255, 255, 255],
            fontStyle: "bold"
          });
          if (i === 1) cell.styles.fontStyle = "bold";

          if (isLastPolicy) {
            if (i === table.body.length - 3) cell.styles.fontStyle = "bold";
            if (i === table.body.length - 1) {
              Object.assign(cell.styles, {
                lineWidth: { top: 0.5, bottom: 0.5 },
                lineColor: [255, 165, 0],
                fontStyle: "bold"
              });
            }
          } else if (i === last) {
            cell.styles.fontStyle = "bold";
          }
        },
        didDrawPage: () => pageHeader(doc)
      });

      currentY = doc.lastAutoTable.finalY - 30;

      // Footer final del documento
      if (isLastCurrency && isLastPolicy) {
        if (currentY + 100 > pageHeight) {
          doc.addPage();
          doc.autoTable({ didDrawPage: () => pageHeader(doc) });
          currentY = 15;
        }
        doc.setFontSize(11);
        doc.setFont(undefined!, "bold");
        doc.text("--- --- --- Fin de Estado de Cuenta --- --- ---", 65, currentY + 50);
        doc.addImage(footer, "PNG", 20, 260, 175, 29);
      }
    });
  });

  doc.save("Estado de cuenta.pdf");
};


/**
 * Formatea un número en formato moneda RD$.
 */
const formatAmount = (value: number): string =>
  new Intl.NumberFormat("es-DO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

/**
 * Compara dos fechas (inicio y fin) para ordenar cronológicamente los movimientos.
 */
const sortByDates = (a: StatementResponse, b: StatementResponse): number => {
  const fechaInicioA = new Date(parseDateString(a.sDate.toString()));
  const fechaInicioB = new Date(parseDateString(b.sDate.toString()));
  if (fechaInicioA < fechaInicioB) return -1;
  if (fechaInicioA > fechaInicioB) return 1;

  const fechaFinA = new Date(parseDateString(a.wDate.toString()));
  const fechaFinB = new Date(parseDateString(b.wDate.toString()));
  return fechaFinA.getTime() - fechaFinB.getTime();
};

/**
 * Convierte una fecha en formato "dd/mm/yyyy" a "mm/dd/yyyy".
 */
const parseDateString = (fecha: string): string => {
  const [dia, mes, anio] = fecha.split('/');
  return `${mes}/${dia}/${anio}`;
};
